/**
 * Centralized Icon Component
 * 
 * This component serves as a single source of truth for all icons in the application.
 * It ensures consistency across all user types and makes icon management easier.
 * 
 * Usage: <Icon name="user" size={20} className="text-blue-500" />
 */

import React from 'react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Bell,
  BookOpen,
  Bot,
  Brain,
  Briefcase,
  Building,
  Building2,
  Bus,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Clipboard,
  Compass,
  Construction,
  CreditCard,
  Crown,
  DollarSign,
  Download,
  Droplets,
  Edit,
  Eye,
  EyeOff,
  FileCheck,
  FileText,
  Filter,
  Globe,
  Grid3X3,
  Headphones,
  Home,
  Info,
  Loader,
  Lock,
  LogOut,
  Mail,
  Map,
  MapPin,
  Menu,
  Music,
  Phone,
  Plane,
  Plus,
  RefreshCw,
  Rocket,
  Ruler,
  Save,
  Search,
  Send,
  Settings,
  Share2,
  Shield,
  Smartphone,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Upload,
  User,
  UserCheck,
  UserPlus,
  Users,
  Wifi,
  Wrench,
  X,
  XCircle,
  Zap,
  Package,
  Ticket,
  GitMerge,
  PenLine
} from 'lucide-react';

// Import custom icons
import MasterTourLogo from './MasterTourLogo';
import VIPAllAccessIcon from './VIPAllAccessIcon';

/**
 * Icon mapping - maps string names to React components
 * This ensures consistent icon usage across the entire application
 */
const iconMap = {
  // Navigation & Actions
  'alert-circle': AlertCircle,
  'alert-triangle': AlertTriangle,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'home': Home,
  'menu': Menu,
  'music': Music,
  'x': X,
  'send': Send,
  'save': Save,
  'search': Search,
  'filter': Filter,
  'edit': Edit,
  
  // User & People
  'user': User,
  'users': Users,
  'user-check': UserCheck,
  'user-plus': UserPlus,
  
  // Business & Organization
  'building': Building,
  'building2': Building2,
  'briefcase': Briefcase,
  'bus': Bus,
  
  // Data & Charts
  'bar-chart': BarChart3,
  'trending-up': TrendingUp,
  
  // Files & Documents
  'file-check': FileCheck,
  'file-text': FileText,
  'upload': Upload,
  'download': Download,
  'clipboard': Clipboard,
  
  // Communication
  'mail': Mail,
  'phone': Phone,
  'bell': Bell,
  'book-open': BookOpen,
  'brain': Brain,
  'share': Share2,
  'share-2': Share2,
  
  // Status & Feedback
  'check-circle': CheckCircle,
  'x-circle': XCircle,
  'clock': Clock,
  'loader': Loader,
  'star': Star,
  'shield': Shield,
  
  // Location & Travel
  'map-pin': MapPin,
  'map': Map,
  'plane': Plane,
  'globe': Globe,
  'compass': Compass,
  
  // Date & Time
  'calendar': Calendar,
  
  // Financial
  'dollar-sign': DollarSign,
  'credit-card': CreditCard,
  
  // Security & Privacy
  'eye': Eye,
  'eye-off': EyeOff,
  'lock': Lock,
  'logout': LogOut,
  
  // Settings & Tools
  'settings': Settings,
  'wrench': Wrench,
  'droplets': Droplets,
  'sparkles': Sparkles,
  'refresh': RefreshCw,
  'plus': Plus,
  
  // Technology
  'smartphone': Smartphone,
  'wifi': Wifi,
  'zap': Zap,
  
  // Other
  'construction': Construction,
  'rocket': Rocket,
  'info': Info,
  'package': Package,
  'ticket': Ticket,
  'crown': Crown,
  'headphones': Headphones,
  'activity': BarChart3,  // Alias for uptime status
  'bot': Bot,  // AI Operations
  'target': Target,
  'grid': Grid3X3,
  'ruler': Ruler,
  
  // Custom Icons
  'master-tour': MasterTourLogo,
  'vip-access': VIPAllAccessIcon,
  'vip': VIPAllAccessIcon, // Alias
  'green-room': VIPAllAccessIcon, // Alias for Green Room
  'git-merge': GitMerge,
  'pen-line': PenLine,
};

/**
 * Icon Component
 * @param {string} name - Icon name from iconMap
 * @param {number} size - Icon size (default: 24)
 * @param {string} className - Additional CSS classes
 * @param {object} props - Additional props passed to icon component
 */
const Icon = ({ name, size = 24, className = '', ...props }) => {
  const IconComponent = iconMap[name];
  
  if (!IconComponent) {
    console.warn(`Icon "${name}" not found in iconMap. Available icons:`, Object.keys(iconMap).join(', '));
    // Return a fallback icon or null
    return <AlertCircle size={size} className={`${className} text-red-500`} {...props} />;
  }
  
  // Handle custom icons that might use different prop names
  if (name === 'master-tour') {
    return <IconComponent width={size} height={size} size={size} className={className} {...props} />;
  }
  
  if (name === 'vip-access' || name === 'vip' || name === 'green-room') {
    return <IconComponent size={size} className={className} {...props} />;
  }
  
  // Standard Lucide icons
  return <IconComponent size={size} className={className} strokeWidth={2} {...props} />;
};

/**
 * Helper function to get all available icon names
 * Useful for documentation and debugging
 */
export const getAvailableIcons = () => Object.keys(iconMap).sort();

/**
 * Helper function to check if an icon exists
 */
export const hasIcon = (name) => name in iconMap;

export default Icon;
